import {SurveyState} from 'client/common/store/reducers/survey/types';
import {
    AccessList,
    AccessListUser,
    AgencyClient,
    AudienceCategoryInfo,
    AudienceInterestInfo,
    AudienceRequest,
    AudienceRequestUpdate,
    AudienceSegmentInfo,
    BillingPayer,
    Box,
    ConnectionComponent,
    Connections,
    ConnectionScope,
    CopySurveyOptions,
    Currency,
    Customer,
    Experiments,
    Features,
    GeoCountry,
    Group,
    LogicNode,
    LogicScheme,
    MetrikaCounter,
    ModerationVerdict,
    Question,
    QuestionImageOption,
    QuestionOption,
    QuestionUpdate,
    Resource,
    Revision,
    RevisionContext,
    Survey,
    SurveyList,
    SurveyPackage,
    SurveyPaymentStatus,
    SurveyPrice,
    SurveySettings,
    SurveySlug,
    SurveyType,
    TableOptionHeader,
    TemplateWelcome,
    UserProfile,
    UXContext,
    Widget,
    WidgetUpdate
} from 'client/common/types';
import {MetricsStatistics} from 'client/common/types/brandlift/types';

export interface ApiClientParams {
    host?: string;
    supplierHost?: string;
    path: string;
    credentials?: any;
    retries?: any;
    headers?: any;
}

export interface WithSlug {
    slug: SurveySlug;
}

export interface WithRevisionId {
    revisionId: Revision['id'];
}

export interface SuccessfulResponse<T = undefined> {
    status: 200 | 201 | 204;
    data: T;
}

export interface ErrorResponse {
    status: number;
    data: {error: string};
    reason: string;
}

export interface UploadTagsErrorResponse extends ErrorResponse {
    data: {
        error: string;
        allowed: number;
        requested: number;
        error_code: number;
    };
}

export type Response<T> = T | ErrorResponse;

export type UploadTagsGeneralResponse<T> = T | UploadTagsErrorResponse;

export type MethodReturnValue<T> = Promise<Response<T>>;

export function isErrorResponse<T>(
    response: SuccessfulResponse<T> | ErrorResponse
): response is ErrorResponse {
    return (response as ErrorResponse).reason !== undefined;
}

export interface SurveyPurpose {
    selected: string;
    isTemplate: boolean;
    isBox: boolean;
}

export interface CreateSurveyParams {
    survey: {
        audience: Pick<AudienceRequest, 'type' | 'size'>;
        box?: Box;
        clientId?: string;
        isTemporary?: boolean;
        name?: string;
        purpose?: SurveyPurpose;
        accountId?: number;
        settings?: SurveySettings;
    };
    apiLocation?: 'brandlift' | 'survey';
}

export type CreateSurveyResponse = SuccessfulResponse<
    Pick<Survey, 'name' | 'revisions' | 'settings' | 'slug' | 'state' | 'updatedAt'>
>;

export interface BindSurveyParams extends WithSlug {
    clientId?: string;
}

export type BindSurveyResponse = SuccessfulResponse;

export enum SurveysFetchFormat {
    Quicksurvey = 'quicksurvey',
    Agency = 'agency',
    Creative = 'creative'
}

export interface SurveysFetchParams {
    clientId?: string;
    format: SurveysFetchFormat;
    minCount: number;
    name?: string;
    paginationKey?: string;
    state?: SurveyState | SurveyState[];
    surveyType?: SurveyType | SurveyType[];
    orgIds?: number;
}

export interface CreateRevisionParams extends WithSlug {
    revisionId?: Revision['id'];
}

export type RevisionResponse = SuccessfulResponse<Revision>;

export type RevisionContextResponse = SuccessfulResponse<RevisionContext>;

export type GetSurveyResponse = SuccessfulResponse<Survey>;

export type GetSurveysResponse = SuccessfulResponse<SurveyList>;

export interface UpdateSurveyParams extends WithSlug {
    state: SurveyState;
}

export type UpdateSurveyResponse = SuccessfulResponse;

export interface UpdateSurveyBulkParams extends WithSlug {
    apiLocation?: string;
    data: any;
}

export type UpdateSurveyBulkResponse = SuccessfulResponse;

export type GetAudienceRequestResponse = SuccessfulResponse<AudienceRequest>;

export type UpdateAudienceRequestParams = AudienceRequestUpdate & WithSlug;

export type UpdateAudienceRequestResponse = SuccessfulResponse;

export type GetAudienceCategoriesResponse = SuccessfulResponse<{
    categories: AudienceCategoryInfo[];
}>;

export type GetAudienceInterestsResponse = SuccessfulResponse<{
    interests: AudienceInterestInfo[];
}>;

export type GetAudienceSegmentsResponse = SuccessfulResponse<{
    segments: AudienceSegmentInfo[];
}>;

export interface GetSurveyStatisticsParams extends WithSlug {
    filter?: {key: string; value: string};
}

export type GetSurveyStatisticsResponse = SuccessfulResponse<MetricsStatistics>;

export type PublishSurveyParams = WithRevisionId & WithSlug;

export type PublishSurveyResponse = SuccessfulResponse;

export type SetAsTemplateParams = WithSlug & TemplateWelcome;

export type SetAsTemplateResponse = SuccessfulResponse;

export type CopySurveyParams = CopySurveyOptions &
    WithSlug & {
        name: string;
    };

export type CopySurveyResponse = SuccessfulResponse<
    {
        audience: Omit<AudienceRequest, 'questionsLimit'>;
        box?: any;
        clientId?: string;
        clientName?: string;
        cost: SurveyPrice;
        name: string;
        revisions: any[];
        settings: any;
        state: SurveyState;
        surveyType: SurveyType;
        updatedAt: string;
    } & WithSlug
>;

export type GetUserProfileResponse = SuccessfulResponse<{profile: UserProfile}>;

export type UpdateUserProfileParams = UserProfile;
export type UpdateUserProfileResponse = SuccessfulResponse;

export type GetAccessListParams = WithSlug;

export type GetAccessListResponse = SuccessfulResponse<AccessList>;

export type GetSurveyPriceResponse = SuccessfulResponse<SurveyPrice>;

export type GetPayersResponse = SuccessfulResponse<BillingPayer[]>;

export type CreatePaymentLinkResponse = SuccessfulResponse<{
    redirect: string;
}>;

export type RefundSurveyResponse = Response<
    SuccessfulResponse<{
        amount_to_refund: number;
    }>
>;

export type GetSurveyPaymentStatusResponse = SuccessfulResponse<SurveyPaymentStatus>;

export interface CreatePaymentLinkParams extends WithSlug {
    payer?: BillingPayer;
}

export type RefundSurveyParams = WithSlug;

export type GetAgencyClientsResponse = SuccessfulResponse<AgencyClient[]>;

export type SyncAgencyClientsResponse = GetAgencyClientsResponse;

export type CreateGroupParams = WithRevisionId &
    WithSlug & {
        groupId?: Group['id'];
        isScreening?: boolean;
    };

export type CreateGroupResponse = SuccessfulResponse<Group>;

export type CopyPageParams = WithRevisionId &
    WithSlug & {
        pageId: Group['id'];
    };

export type CopyQuestionResponse = SuccessfulResponse<Question>;
export type CopyWidgetResponse = SuccessfulResponse<Widget>;

export type CreateQuestionParams = QuestionUpdate &
    WithRevisionId &
    WithSlug & {
        insertAt?: number;
        pageId: Group['id'];
    };

export type CreateBoxCustomQuestionParams = QuestionUpdate &
    WithRevisionId &
    WithSlug & {
        parentQuestionIndex?: number;
    };

export type RemoveBoxCustomQuestionParams = WithRevisionId &
    WithSlug & {
        id: Question['id'];
    };

export type RemoveUxStepParams = WithRevisionId &
    WithSlug & {
        index: number;
    };

export type CopyBoxCustomQuestionParams = RemoveBoxCustomQuestionParams;

export type CreateBoxCustomQuestionResponse = SuccessfulResponse<UXContext>;

export type CopyBoxCustomQuestionResponse = CreateBoxCustomQuestionResponse;

export type CreateQuestionResponse = SuccessfulResponse<Question>;
export type CreateWidgetParams = WidgetUpdate &
    WithRevisionId &
    WithSlug & {
        insertAt?: number;
        pageId: Group['id'];
    };
export type CreateWidgetResponse = SuccessfulResponse<Widget>;

export type CopyPageItemParams = WithRevisionId &
    WithSlug & {
        id: Question['id'] | Widget['id'];
        pageId: Group['id'];
    };

export type RemoveItemParams = WithRevisionId &
    WithSlug & {
        id: Question['id'] | Widget['id'];
        force: boolean;
    };

export interface UpdateQuestionOptions {
    force: boolean;
    refetch: boolean;
}

export type UpdateQuestionParams = UpdateQuestionOptions &
    WithRevisionId &
    WithSlug & {
        id: Question['id'];
        data: QuestionUpdate;
    };

export type UpdateWidgetParams = WithRevisionId &
    WithSlug & {
        id: Widget['id'];
        data: WidgetUpdate;
        force: boolean;
    };

export type UpdatePageParams = WithRevisionId &
    WithSlug & {
        id: Group['id'];
        data: any;
        force: boolean;
    };

export type GetConnectionsParams = WithRevisionId &
    WithSlug & {
        componentIds: (Question['id'] | Widget['id'] | QuestionOption['id'] | Group['id'])[];
        componentType: ConnectionComponent;
        scope: ConnectionScope;
    };

export type GetConnectionsResponse = SuccessfulResponse<Connections>;

export interface AddUserToAccessListParams extends WithSlug {
    login: string;
}

export type AddUserToAccessListResponse = SuccessfulResponse<AccessListUser>;

export interface RemoveUserFromAccessListParams extends WithSlug {
    login: string;
}

export type RemoveUserFromAccessListResponse = SuccessfulResponse;

export type AddOptionParams = WithRevisionId &
    WithSlug & {
        questionId: Question['id'];
    };

export type AddOptionResponse = SuccessfulResponse<QuestionOption>;

export type RemoveOptionParams = WithRevisionId &
    WithSlug & {
        questionId: Question['id'];
        optionId: QuestionOption['id'];
    };

export type UpdateOptionParams = WithRevisionId &
    WithSlug & {
        questionId: Question['id'];
        optionId: QuestionOption['id'];
        data: Partial<QuestionOption | QuestionImageOption>;
    };

export type UpdateOptionsAsBulkParams = WithRevisionId &
    WithSlug & {
        questionId: Question['id'];
        data: {options: string[]; append: boolean};
    };

export type ChangePositionParams = WithRevisionId &
    WithSlug & {
        child:
            | {
                  type: 'question';
                  id: Question['id'];
              }
            | {
                  type: 'widget';
                  id: Widget['id'];
              };
        pageId: Group['id'];
        position: number;
    };

export type UpdateComponentsParams = WithRevisionId &
    WithSlug & {
        components: LogicNode[];
        fields: {showIf: LogicScheme | null};
    };

export type GetMetrikaCountersResponse = SuccessfulResponse<{
    counters: MetrikaCounter[];
}>;

export type UpdateRevisionParams = WithRevisionId &
    WithSlug & {
        data: Partial<Revision>;
    };

export interface DenyModerationParams {
    slugs: SurveySlug[];
    reasons?: string[];
    withEmail?: boolean;
}

export type DenyModerationResponse = SuccessfulResponse;

export type GetWalletBalanceResponse = SuccessfulResponse<{
    balance: number;
    uid: number;
    currency: Currency | null;
}>;

export type GetInterviewBalanceResponse = SuccessfulResponse<{
    free: number;
    paid: number;
    total: number;
}>;

export interface CreateWalletTopUpLinkParams {
    amount: number;
    payer?: BillingPayer;
    retpath: string;
}

export type CreateWalletTopUpLinkResponse = SuccessfulResponse<{
    redirect: string;
}>;

export interface CreateSurveyInterviewPayLinkParams extends WithSlug {
    interviewCount: number;
    payer?: BillingPayer;
    retpath: string;
}

export interface CreateWalletInterviewPayLinkParams {
    amount: number;
    payer?: BillingPayer;
    retpath: string;
}

export type CreateSurveyInterviewPayLinkParamsResponse = SuccessfulResponse<{
    redirect: string;
}>;

export type GetSurveyInterviewCountParams = WithSlug;

export type GetSurveyInterviewCountParamsResponse = SuccessfulResponse<{
    free_count: number;
    payed_count: number;
    count: number;
    survey_id: number;
}>;

export interface GetInterviewCostParams {
    interviewCount: number;
}

export interface SetSurveyTotalCostParams extends WithSlug {
    totalCost: number;
}
export type GetInterviewCostParamsResponse = SuccessfulResponse<{
    count: number;
    currency: Currency;
    value: number;
    vat: boolean;
}>;

export type PayWithWalletParams = WithSlug;

export interface PayInterviewsWithWalletParams extends WithSlug {
    count: number;
}

export interface TopUpInterviewsFromWalletParams {
    interview_count: number;
}

export interface PayFromInterviewsWalletParams extends WithSlug {
    finished: number;
    unfinished: number;
}

export type PayWithWalletResponse = SuccessfulResponse;

export type PayInterviewWithWalletResponse = SuccessfulResponse;

export type TopUpInterviewsFromWalletResponse = SuccessfulResponse;

export type PayFromInterviewsWalletResponse = SuccessfulResponse<{
    redirect: string;
}>;

export type GetSurveyFeaturesParams = WithSlug;

export type GetSurveyFeaturesResponse = SuccessfulResponse<Features>;

export type GetUserFeaturesResponse = SuccessfulResponse<Features>;

// export type GetUserCountryResponse = SuccessfulResponse<{countryId: number | undefined}>;

export interface SetUserCurrencyParams {
    currency: Currency;
    countryId: GeoCountry;
}
export type GetUserAvailableCountriesResponse = SuccessfulResponse<{
    countries: Record<GeoCountry, Currency[]>;
}>;

export interface GetSurveyStatParams extends WithSlug {
    type: 'creative' | 'events' | 'progress' | 'answers' | 'clicks' | 'stat';
    params?: Record<string, any>;
}

interface SurveyStatResponse {
    finished_events: number;
    started_events: number;
    median_time: number;
    conversion: number | null;
    available_interviews: number;
}

interface CreativeStatResponse {
    index: {
        value: number;
        to_bench: 'over' | 'below' | 'equal';
    };
}

export type GetSurveyStatResponse = SuccessfulResponse<CreativeStatResponse | SurveyStatResponse>;

export type GetExperimentsResponse = SuccessfulResponse<Experiments>;

export type SurveyPackageInfoResponse = SuccessfulResponse<{
    count: number;
}>;

export type SurveyBindedPackageResponse = SuccessfulResponse<{
    packageId?: number;
}>;

export interface FetchPackageListParams {
    login?: string;
    customerId?: number;
    paginationKey?: string;
}

export type FetchPackageListResponse = SuccessfulResponse<SurveyPackage[]>;

export type FetchCustomersResponse = SuccessfulResponse<Customer[]>;

export interface CreateCustomerParams {
    name: string;
}
export type CreateCustomerResponse = SuccessfulResponse<Customer>;

export interface CreateSurveyPackageParams {
    customerId: number;
    logins: string[];
    count: number;
    expires: string;
    description?: string;
}

export type CreateSurveyPackageResponse = SuccessfulResponse<number>;

export interface AddPackageUsersParams {
    packageId: number;
    logins: string[];
}
export type AddPackageUsersResponse = SuccessfulResponse;

export interface FetchPackageParams {
    packageId: number;
}
export type FetchPackageResponse = SuccessfulResponse<SurveyPackage>;

export interface PaymentAccount {
    id: number;
    abc_id: number;
    abc_name: string;
    responsible_staff_login: string;
    cfo: string;
    external_login: string;
    agency_client_id: number | null;
    client_id: number;
    direct_canvas_video_id: string;
    direct_yav_secret_id: string;
    direct_yav_secret_key: string;
    metrika_yav_secret_id: string;
    metrika_yav_secret_key: string;
}

export type AddPaymentAccountParams = Omit<PaymentAccount, 'id'>;

export interface CreateResourceParams extends WithSlug {
    type: 'vh_video';
    resourceData: {
        videoMetaId: string;
    };
    clientId?: string;
}
export type CreateResourceResponse = Response<SuccessfulResponse<{resourceId: string}>>;
export type FetchResourceResponse = Response<SuccessfulResponse<Resource>>;

export interface VideoUploadData {
    videoMetaId: string;
    videoFileId: string;
    location: string;
}
export type GetVHVideoUploadLinkResponse = Response<SuccessfulResponse<VideoUploadData>>;
export type GetVHVideoInfoResponse = Response<SuccessfulResponse<{duration?: number}>>;
export type SurveyCreatorResponse = SuccessfulResponse<{login: string}>;

export type SaveUserExperimentsResponse = SuccessfulResponse<{status: string}>;
export interface SaveUserExperimentsParams {
    experiments: Record<string, 'on' | 'off'>;
}

export interface ModerationTooltip {
    id: number;
    title: string;
}

export type FetchModerationTooltipsResponse = SuccessfulResponse<ModerationTooltip[]>;

interface DemandAssessmentSettings {
    type: {
        values: {
            [key: string]: {
                translation: {
                    keyset: string;
                    key: string;
                };
            };
        };
    };
}

type PurposeConfigSettings = DemandAssessmentSettings;

export interface PurposeConfig {
    context: {
        template: {
            [key: string]: {
                placeholder?: {
                    keyset: string;
                    key: string;
                };
            };
        };
        settings: PurposeConfigSettings;
    };
}

export type FetchPurposeConfigResponse = SuccessfulResponse<{config: PurposeConfig}>;
export type FetchAccountListResponse = SuccessfulResponse<
    {id: number; abc_name: string; external_login: string}[]
>;

export interface UpdateSurveyPaymentAccountParams extends WithSlug {
    accountId: number;
}
export type UpdateSurveyPaymentAccountResponse = SuccessfulResponse;

export interface EditUserFeaturesParams {
    logins: string[];
    features: string[];
}

export type GetModerationVerdictResponse = SuccessfulResponse<ModerationVerdict>;

export type GetUnlockedSurveysCountParams = WithSlug;

export interface UnlockedSurveysCount {
    finished: number;
    unfinished: number;
    has_answers: number;
}

export interface UploadedTags {
    new_tags: number;
    unique_tags_before: number;
    unique_tags_now: number;
    updated_rows: number;
}

export type GetUnlockedSurveysCountResponse = SuccessfulResponse<UnlockedSurveysCount>;

export type UploadTagsResponse = UploadTagsGeneralResponse<SuccessfulResponse<UploadedTags>>;

export type AddTableOptionParams = AddOptionParams;
export type AddTableOptionResponse = SuccessfulResponse<TableOptionHeader>;

export type RemoveTableOptionParams = WithRevisionId &
    WithSlug & {
        questionId: Question['id'];
        index: number;
    };

export type UpdateTableOptionParams = WithRevisionId &
    WithSlug & {
        questionId: Question['id'];
        index: number;
        data: Partial<TableOptionHeader>;
    };

export type ToggleScreeningParams = WithRevisionId & WithSlug;

export type CreateRespondentsListResponse<T> = SuccessfulResponse<T>;

export type UpdateLabelsParams = WithSlug & {
    data: {
        changes: Record<string, string>;
    };
};
